import {Link} from '@wandb/weave/common/util/links';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext} from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';

import {envIsPublicCloud} from '../../../../config';
import {
  EntityIntegrationsDocument,
  Integration,
  SlackIntegration as GQLSlackIntegration,
} from '../../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useTeamMember} from '../../../../state/graphql/teamMembers';
import {useViewer} from '../../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../../util/admin';
import {propagateErrorsContext} from '../../../../util/errors';
import * as urls from '../../../../util/urls';
import {teamSettings} from '../../../../util/urls';
import {BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {
  isSlackIntegration,
  SlackIntegration,
  toSlackIntegration,
} from '../../../SlackIntegration/utils';
import {InstrumentedLoader} from '../../../utility/InstrumentedLoader';
import {NotificationTriggeredAction, Trigger} from '../TriggersTable/utilTypes';
import {NotificationConfig, useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {eventStoreDeps} from './EventStore';

const formatNotificationOptionLabel = (
  notificationOption: NotificationOptionType | null
) => {
  if (notificationOption == null) {
    return;
  }
  const {label} = notificationOption;
  return (
    <S.SelectOption>
      <S.SelectOptionLabelText title={label}>{label}</S.SelectOptionLabelText>
    </S.SelectOption>
  );
};

type NotificationOptionType = {
  label: string;
  value: SlackIntegration;
};

export const NotificationSection = ({
  integrations,
  slackIntegrationsLoading,
}: {
  integrations: SlackIntegration[];
  slackIntegrationsLoading: boolean;
}) => {
  const viewer = useViewer();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {updateNotificationConfig, entityName} = useActionConfigStore(
    useShallow(s => s)
  );

  const {teamSlackIntegration, loading, teamSettingsUrl} = useActionConfigStore(
    useShallow(s => s.notification)
  );

  const orgName = envIsPublicCloud
    ? selectedAccount?.name ?? ''
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const viewerIsAdmin = useAdminModeActive();
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: entityName,
  });

  // Check if still loading, or if no slack integrations are registered
  if (slackIntegrationsLoading) {
    return (
      <InstrumentedLoader active name="create-trigger-drawer-notification" />
    );
  }
  if (integrations.length === 0) {
    return <S.ErrorText>No slack integrations available</S.ErrorText>;
  }

  const notificationOptions: NotificationOptionType[] = integrations.map(w => {
    return {
      value: w,
      label: w.channelName,
    };
  });
  const defaultNotificationOption = notificationOptions.find(
    w => w.value.id === teamSlackIntegration?.id
  );

  return (
    <>
      <Tailwind>
        <S.BannerWithMargin variant="info">
          <BannerFlexWrapper>
            <Icon name="lightbulb-info" />
            <BannerTextWrapper>
              Slack channels can be managed in your team settings by team
              admins.
            </BannerTextWrapper>
          </BannerFlexWrapper>
          {
            // Only show the team settings link to admins, since they're the only ones who can make changes.
            (viewerIsTeamAdmin || viewerIsAdmin) && (
              <button className="flex items-center whitespace-nowrap border-none bg-transparent font-semibold text-moon-800 hover:cursor-pointer hover:text-moon-950">
                <Link to={urls.teamSettings(entityName ?? '')}>
                  Team settings
                </Link>
              </button>
            )
          }
        </S.BannerWithMargin>
      </Tailwind>
      <S.LabelRow>
        <S.Label>Slack channel</S.Label>
      </S.LabelRow>
      <Select<NotificationOptionType | null>
        options={notificationOptions}
        placeholder="Select a Slack channel..."
        defaultValue={defaultNotificationOption}
        onChange={(d: NotificationOptionType | null) => {
          if (d) {
            updateNotificationConfig({teamSlackIntegration: d.value});
          }
        }}
        autoFocus={true}
        formatOptionLabel={formatNotificationOptionLabel}
      />
    </>
  );
};

// ------------------------------------------------------------------------------
// NOTE: We are utlimately moving towards the implementation below, but in order
// to quickly support multiple Slack integrations, we adopted the existing,
// older implementation in WebhookConfiguration.tsx at the time.  This means
// we reverted to a legacy implementation, which relies on a single action store
// for CTD.
//
// For clarity: The goal is to move to separate config stores for
// each action, and the boilerplate below is retained from that implementation.
export const useNotificationStore = create<NotificationConfig>()(() => ({
  teamSlackIntegration: undefined,
  loading: false,
  teamSettingsUrl: undefined,
}));
export const NotificationActions = {
  initAsync: async (teamName: string) => {
    useNotificationStore.setState({loading: true});
    const teamIntegration = await getSlackIntegrationForEntity(teamName);
    useNotificationStore.setState({
      loading: false,
      teamSlackIntegration: teamIntegration,
      teamSettingsUrl: teamName ? teamSettings(teamName) : undefined,
    });
  },
  setTeamName: async (teamName: string | undefined) => {
    useNotificationStore.setState({
      loading: true,
    });
    if (teamName) {
      const teamIntegration = await getSlackIntegrationForEntity(teamName);
      useNotificationStore.setState({
        loading: false,
        teamSlackIntegration: teamIntegration,
        teamSettingsUrl: teamName ? teamSettings(teamName) : undefined,
      });
    } else {
      useNotificationStore.setState({
        loading: false,
        teamSlackIntegration: undefined,
        teamSettingsUrl: undefined,
      });
    }
  },
};

const getFirstSlackIntegration = (entityIntegrations: {
  edges: {
    node: Integration;
  }[];
}): SlackIntegration | undefined => {
  const integrations: {node: GQLSlackIntegration}[] =
    entityIntegrations.edges.filter((e: {node: Integration}) =>
      isSlackIntegration(e.node)
    ) as {node: GQLSlackIntegration}[];
  if (integrations?.length > 1) {
    console.log('saw more than 1 slack integration!', entityIntegrations);
  }
  if (integrations.length > 0) {
    const firstIntegration = integrations[0].node;
    return toSlackIntegration(firstIntegration);
  }
  return undefined;
};

const getSlackIntegrationForEntity = async (
  entityName: string
): Promise<SlackIntegration | undefined> => {
  const entityIntegrations = await eventStoreDeps.apolloQuery({
    query: EntityIntegrationsDocument,
    variables: {
      name: entityName,
    },
    context: propagateErrorsContext(),
  });

  return getFirstSlackIntegration(
    entityIntegrations.data?.entity?.integrations
  );
};

// This is used when we have an already created trigger in the UI
export const getNotificationConfig = (trigger: Trigger): NotificationConfig => {
  const emptyConfig: NotificationConfig = {
    teamSlackIntegration: undefined,
    loading: false,
    teamSettingsUrl: undefined,
  };

  const triggerAction = trigger.triggeredAction;
  if (
    triggerAction?.actionType !== 'NotificationTriggeredAction' ||
    !triggerAction.actionConfig
  ) {
    return emptyConfig;
  }
  const notificationConfig =
    triggerAction.actionConfig as NotificationTriggeredAction;

  return {
    teamSlackIntegration: {
      id: notificationConfig.integrationID,
      teamName: '',
      channelName: '',
    },
    loading: false,
    teamSettingsUrl: undefined,
  };
};
